import { PlanInfoBadge } from "#/components/views/planinfo/PlanInfoBadge"
import React, { useMemo } from "react"
import { COMPANY_DEDUCTIBLE_STATUS, useUserDeductible } from "../../context/user/DeductibleContext"
import { FormattedMessage } from "react-intl"
import { reimbursementCategoryNameMessageKeys } from "../../views/reimbursements/reimbursementCategoryNameMessageKeys"
import { Box, BoxProps } from "@carrotfertility/carotene-core"
import { getCategoryCoverage } from "../../../utils/CategoryCoverage"
import { Subsidy, OutOfPocketMaxState } from "#/lib/carrot-api/types/Subsidy"

interface PlanInfoBadgeContainerProps {
  subsidy: Subsidy
  allSubsidies: Subsidy[]
  sx?: BoxProps["sx"]
  stackVertically?: boolean
}

const PlanInfoBadgeContainer = ({
  subsidy,
  allSubsidies,
  sx,
  stackVertically = false
}: PlanInfoBadgeContainerProps): JSX.Element => {
  const { companyDeductibleStatus, hasCMD } = useUserDeductible()
  const companyHasExternalDeductible = useMemo(
    () => companyDeductibleStatus === COMPANY_DEDUCTIBLE_STATUS.EXTERNAL,
    [companyDeductibleStatus]
  )
  const companyHasCMD = useMemo(
    () => companyDeductibleStatus === COMPANY_DEDUCTIBLE_STATUS.CARROT_MANAGED_DEDUCTIBLE,
    [companyDeductibleStatus]
  )
  const hasQmeCategory = getCategoryCoverage(allSubsidies).qme
  const categoryNameMessageKey = subsidy.reimbursementCategoryNameMessageKey.toString()
  const isOtherCategory = categoryNameMessageKey === reimbursementCategoryNameMessageKeys.OTHER
  const categorySupportsDeductibles =
    categoryNameMessageKey !== reimbursementCategoryNameMessageKeys.ADOPTION &&
    categoryNameMessageKey !== reimbursementCategoryNameMessageKeys.GC &&
    categoryNameMessageKey !== reimbursementCategoryNameMessageKeys.ADOPTION_AND_GC &&
    !(isOtherCategory && hasQmeCategory)

  const showDeductibleMayApply = isOtherCategory && !hasQmeCategory

  const hasOopmDependentRule = useMemo(
    () => subsidy.reimbursementRules?.some((rule) => rule.outOfPocketMaxState !== OutOfPocketMaxState.ANY) ?? false,
    [subsidy.reimbursementRules]
  )

  const showDeductibleBadge = useMemo(
    () => categorySupportsDeductibles && (companyHasExternalDeductible || (companyHasCMD && hasCMD)),
    [categorySupportsDeductibles, companyHasExternalDeductible, companyHasCMD, hasCMD]
  )
  const showCostShareBadge = subsidy?.hasCostShare

  const deductibleBadgeModalType = useMemo(() => {
    if (companyHasCMD) return "cmd"
    if (companyHasExternalDeductible && hasOopmDependentRule) return "emdAndOopm"
    return "emd"
  }, [companyHasCMD, companyHasExternalDeductible, hasOopmDependentRule])

  const deductibleBadgeLabel = useMemo(() => {
    if (showDeductibleMayApply) {
      return <FormattedMessage defaultMessage="Deductible may apply" />
    }
    if (companyHasExternalDeductible && hasOopmDependentRule) {
      return <FormattedMessage defaultMessage="Deductible and OOPM apply" />
    }
    return <FormattedMessage defaultMessage="Deductible applies" />
  }, [showDeductibleMayApply, companyHasExternalDeductible, hasOopmDependentRule])

  if (!showDeductibleBadge && !showCostShareBadge) {
    return <></>
  }

  return (
    <Box
      display="flex"
      flexDirection={stackVertically ? "column" : "row"}
      gap={(theme) => theme.tokens.spacing.md}
      sx={sx}
    >
      {showDeductibleBadge && (
        <PlanInfoBadge subsidy={subsidy} modalType={deductibleBadgeModalType}>
          {deductibleBadgeLabel}
        </PlanInfoBadge>
      )}
      {showCostShareBadge && (
        <PlanInfoBadge subsidy={subsidy} modalType="costShare">
          <FormattedMessage defaultMessage="Cost sharing applies" />
        </PlanInfoBadge>
      )}
    </Box>
  )
}

export { PlanInfoBadgeContainer }
